import React, { useState, useEffect, useRef } from 'react';
import './varanasi-package.scss';
import { Container, Grid } from '@mui/material';
import {
  LocationOn,
  Schedule,
  Search,
  KeyboardArrowDown,
  CheckCircle,
  Check,
} from '@mui/icons-material';

const departureCities = [
  'New Delhi',
  'Varanasi',
  'Lucknow',
  'Mumbai',
  'Ahmedabad',
  'Bangalore',
  'Kolkata',
  'Hyderabad',
  'Chandigarh',
  'Jaipur',
  'Patna',
];

const durationOptions = [
  { value: 'all', label: 'Any duration' },
  { value: '2d', label: '2 Days / 1 Night' },
  { value: '3d', label: '3 Days / 2 Nights' },
  { value: '4d', label: '4 Days / 3 Nights' },
  { value: '5d', label: '5 Days / 4 Nights' },
];

function CustomDropdown({ icon: Icon, value, options, onChange, placeholder }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const selectedOption = options.find((opt) =>
    typeof opt === 'object' ? opt.value === value : opt === value
  );

  const displayLabel = selectedOption
    ? typeof selectedOption === 'object'
      ? selectedOption.label
      : selectedOption
    : placeholder || 'Select';

  const handleSelect = (option) => {
    const val = typeof option === 'object' ? option.value : option;
    onChange(val);
    setIsOpen(false);
  };

  return (
    <div className={`customDropdown ${isOpen ? 'open' : ''}`} ref={dropdownRef}>
      <button
        type="button"
        className="dropdownTrigger"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        {Icon && <Icon className="fieldIcon" />}
        <span className="selectedText">{displayLabel}</span>
        <KeyboardArrowDown className={`arrowIcon ${isOpen ? 'rotated' : ''}`} />
      </button>

      {isOpen && (
        <div className="dropdownMenu" role="listbox">
          {options.map((option, idx) => {
            const optVal = typeof option === 'object' ? option.value : option;
            const optLabel = typeof option === 'object' ? option.label : option;
            const isSelected = optVal === value;

            return (
              <div
                key={idx}
                role="option"
                aria-selected={isSelected}
                className={`dropdownItem ${isSelected ? 'selected' : ''}`}
                onClick={() => handleSelect(option)}
              >
                <span className="itemLabel">{optLabel}</span>
                {isSelected && <Check className="checkIcon" />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function VaranasiPackage() {
  const [departureCity, setDepartureCity] = useState('New Delhi');
  const [selectedDuration, setSelectedDuration] = useState('all');

  // Enquiry form state
  const [enquiryName, setEnquiryName] = useState('');
  const [enquiryPhone, setEnquiryPhone] = useState('');
  const [enquiryPackage, setEnquiryPackage] = useState('Mesmerizing Varanasi with Ayodhya');
  const [enquiryNotes, setEnquiryNotes] = useState('');
  const [enquirySubmitted, setEnquirySubmitted] = useState(false);
  const [enquiryError, setEnquiryError] = useState('');

  // FAQ state
  const [openFaq, setOpenFaq] = useState(null);

  const faqs = [
    {
      id: 1,
      question: 'What is included in a Kashi Vishwanath package?',
      answer:
        'A standard Kashi Vishwanath tour package typically includes hotel accommodation in Varanasi, AC transfers and local transport, Sugam Darshan assistance at Kashi Vishwanath Temple, the evening Ganga Aarti experience, local temple visits, and sightseeing as per the itinerary.',
    },
    {
      id: 2,
      question: 'Does the package include Sugam Darshan at Kashi Vishwanath?',
      answer:
        'Yes, our packages provide assistance with pre-booked Sugam Darshan (VIP pass) through the official temple portal to ensure a smooth, priority darshan of Lord Shiva without long waiting queues.',
    },
    {
      id: 3,
      question: 'Is Ganga Aarti included in the itinerary?',
      answer:
        'Yes, witnessing the world-renowned evening Ganga Aarti at Dashashwamedh Ghat is an essential part of the itinerary. Devotees can comfortably experience the rituals, hymns, and brass lamp aarti from the ghat or an arranged boat.',
    },
    {
      id: 4,
      question: 'Can I visit Kaal Bhairav Temple during the tour?',
      answer:
        'Yes, darshan at the sacred Kaal Bhairav Temple—honored as the Kotwal (guardian deity) of Kashi—is included as per tradition where pilgrims seek blessings before or after visiting Kashi Vishwanath.',
    },
    {
      id: 5,
      question: 'Does the package include Sarnath sightseeing?',
      answer:
        'Yes, Sarnath sightseeing is included as per the itinerary. Devotees and travellers can explore the Dhamek Stupa, the Archaeological Museum with the national emblem, and renowned Buddhist temples.',
    },
  ];

  const toggleFaq = (id) => {
    setOpenFaq((prev) => (prev === id ? null : id));
  };

  const varanasiPackages = [
    {
      id: 1,
      recommended: false,
      stayTag: '2N Varanasi | 1N Ayodhya',
      image: '/assets/varanasi-packages/package-1.webp',
      meta: '3 NIGHTS | PILGRIMAGE',
      title: 'Mesmerizing Varanasi with Ayodhya',
      desc: 'Sugam Darshan, Ganga Aarti Seating & Ram Janmabhoomi Darshan.',
      inclusions: ['Hotel', 'Sightseeing', 'Transfer', 'Meals'],
      oldPrice: '₹ 38,499',
      price: '₹ 21,199',
      priceNote: 'Per person on twin sharing',
    },
    {
      id: 2,
      recommended: true,
      stayTag: '1N Varanasi',
      image: '/assets/varanasi-packages/package-2.webp',
      meta: '1 NIGHTS | PILGRIMAGE',
      title: 'Kashi Darshan – Varanasi Spiritual Escape',
      desc: 'Seek blessings, witness Ganga Aarti, and explore Sarnath.',
      inclusions: ['Hotel', 'Sightseeing'],
      oldPrice: '₹ 8,499',
      price: '₹ 7,499',
      priceNote: 'Per person on twin sharing',
    },
    {
      id: 3,
      recommended: false,
      stayTag: '2N|3D Varanasi',
      image: '/assets/varanasi-packages/package-3.webp',
      meta: '2 NIGHTS | PILGRIMAGE',
      title: 'Kashi Vishwanath Jyotirlinga Darshan Yatra',
      desc: 'VIP Darshan, Mangala Aarti, Ganga Aarti, luxury stays, and daily yoga sessions.',
      inclusions: ['Hotel', 'Sightseeing', 'Transfer', 'Meals'],
      oldPrice: '₹ 1,59,999',
      price: '₹ 1,24,999',
      priceNote: 'Per person on twin sharing',
    },
    {
      id: 4,
      recommended: true,
      stayTag: '2N-3D Varanasi',
      image: '/assets/varanasi-packages/package-4.webp',
      meta: '2 NIGHTS | PILGRIMAGE',
      title: 'Kashi Divya Darshan – Varanasi Spiritual Journey',
      desc: 'Mangala Aarti, boat ride, sacred temple visits, and the mesmerizing Ganga Aarti.',
      inclusions: ['Hotel', 'Sightseeing', 'Transfer'],
      oldPrice: '₹ 16,200',
      price: '₹ 14,999',
      priceNote: 'Per person on twin sharing',
    },
    {
      id: 5,
      recommended: false,
      stayTag: '2N-3D Varanasi',
      image: '/assets/varanasi-packages/package-5.webp',
      meta: '3 NIGHTS | PILGRIMAGE',
      title: 'Kashi Mahadarshan – Complete Varanasi Experience',
      desc: 'Temple visits, the mesmerizing Ganga Aarti, and a sunrise private boat ride.',
      inclusions: ['Hotel', 'Sightseeing', 'Transfer'],
      oldPrice: '₹ 18,400',
      price: '₹ 15,999',
      priceNote: 'Per person on twin sharing',
    },
  ];

  const handleBannerSearch = () => {
    const el = document.getElementById('varanasiPackagesSection');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleBookNow = (title) => {
    setEnquiryPackage(title);
    setEnquirySubmitted(false);
    const el = document.getElementById('enquirySection');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleEnquirySubmit = (e) => {
    e.preventDefault();
    if (!enquiryName.trim() || !enquiryPhone.trim()) {
      setEnquiryError('Please enter your name and mobile number.');
      return;
    }
    setEnquiryError('');
    setEnquirySubmitted(true);
  };

  return (
    <div className="varanasiPackagePage">
      {/* ==========================================
          HERO BANNER
      ========================================== */}
      <section className="vnpHero">
        <Container maxWidth="lg">
          <Grid container>
            <Grid size={{ xs: 12, md: 9, lg: 8 }}>
              <div className="heroInner">
                {/* Subtitle / Location */}
                <span className="heroTagTitle">VARANASI · UTTAR PRADESH</span>

                {/* Main Heading */}
                <h1 className="heroMainHeading">
                  VARANASI TOUR <br />
                  PACKAGES
                </h1>

                {/* Subtext */}
                <p className="heroSubtext">
                  Curated Kashi Yatra itineraries with stay, transfers, darshan and Ganga Aarti experiences.
                </p>

                {/* Pill Search / Selector Card */}
                <div className="bannerSearchCard">
                  {/* Location Selector */}
                  <CustomDropdown
                    icon={LocationOn}
                    value={departureCity}
                    options={departureCities}
                    onChange={setDepartureCity}
                    placeholder="Select City"
                  />

                  {/* Duration Selector */}
                  <CustomDropdown
                    icon={Schedule}
                    value={selectedDuration}
                    options={durationOptions}
                    onChange={setSelectedDuration}
                    placeholder="Select Duration"
                  />

                  {/* Search Button */}
                  <button
                    type="button"
                    className="searchBtn"
                    onClick={handleBannerSearch}
                  >
                    <Search className="btnIcon" />
                    <span>Search</span>
                  </button>
                </div>
              </div>
            </Grid>
          </Grid>
        </Container>
      </section>

      {/* ==========================================
          VARANASI PACKAGES FOR EVERY YATRI
      ========================================== */}
      <section className="varanasiPackagesSection pb-0" id="varanasiPackagesSection">
        <Container maxWidth="lg">
          {/* Section Header */}
          <div className="sectionTitleBlock">
            <h2>Varanasi packages for every yatri</h2>
            <p>
              Handpicked itineraries starting from Varanasi, the spiritual heart of Kashi.
            </p>
          </div>

          {/* Cards Grid */}
          <Grid container spacing={3}>
            {varanasiPackages.map((pkg) => (
              <Grid size={{ xs: 12, md: 4, sm: 6 }} key={pkg.id}>
                <div className="varanasiPackageCard">
                  {/* Top Image Container */}
                  <div className="cardImgBox">
                    <img src={pkg.image} alt={pkg.title} />

                    {/* Recommended Tag */}
                    {pkg.recommended && (
                      <span className="recommendedTag">Recommended</span>
                    )}

                    {/* Stay Duration Tag */}
                    <span className="stayTag">{pkg.stayTag}</span>
                  </div>

                  {/* Card Content */}
                  <div className="cardContentBox">
                    {/* Meta info */}
                    <span className="metaBadge">{pkg.meta}</span>

                    {/* Title */}
                    <h3 className="pkgTitle">{pkg.title}</h3>

                    {/* Description */}
                    <p className="pkgDesc">{pkg.desc}</p>

                    {/* Inclusions Pills */}
                    <div className="inclusionPills">
                      {pkg.inclusions.map((item, idx) => (
                        <span className="pill" key={idx}>
                          {item}
                        </span>
                      ))}
                    </div>

                    {/* Bottom Pricing & CTA */}
                    <div className="cardBottomWrap">
                      <span className="startingFromLabel">Starting from</span>
                      <div className="priceRow">
                        <span className="oldPrice">{pkg.oldPrice}</span>
                        <span className="finalPrice">{pkg.price}</span>
                      </div>
                      <span className="sharingNote">{pkg.priceNote}</span>

                      <button
                        type="button"
                        className="bookNowBtn"
                        onClick={() => handleBookNow(pkg.title)}
                      >
                        Book Now
                      </button>
                    </div>
                  </div>
                </div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </section>

      {/* ==========================================
          WHAT IS USUALLY INCLUDED & ENQUIRY SECTION
      ========================================== */}
      <section className="inclusionsEnquirySection pb-0" id="enquirySection">
        <Container maxWidth="lg">
          <Grid container spacing={2} alignItems="center">
            {/* Left Side: What is usually included */}
            <Grid size={{ xs: 12, md: 5, lg: 7.2 }}>
              <div className="inclusionsContent">
                <h2 className="inclusionsHeading">What is usually included</h2>
                <p className="inclusionsIntro">
                  Organised Kashi Vishwanath itineraries are designed to make the
                  pilgrimage smooth, so devotees can focus on darshan, devotion and
                  exploring the spiritual heritage of Kashi.
                </p>

                {/* 2-Column Exact Layout */}
                <div className="inclusionsColumns">
                  {/* Left sub-column */}
                  <div className="inclusionsCol">
                    <div className="inclusionItem">
                      <span className="bulletDot" />
                      <span className="inclusionText">
                        Accommodation in hotels <br />
                        at Varanasi
                      </span>
                    </div>

                    <div className="inclusionItem">
                      <span className="bulletDot" />
                      <span className="inclusionText">
                        Sugam Darshan at Kashi <br />
                        Vishwanath
                      </span>
                    </div>

                    <div className="inclusionItem">
                      <span className="bulletDot" />
                      <span className="inclusionText">
                        Kaal Bhairav Temple <br />
                        darshan
                      </span>
                    </div>

                    <div className="inclusionItem">
                      <span className="bulletDot" />
                      <span className="inclusionText">
                        Assistance during temple <br />
                        visits
                      </span>
                    </div>
                  </div>

                  {/* Right sub-column */}
                  <div className="inclusionsCol">
                    <div className="inclusionItem">
                      <span className="bulletDot" />
                      <span className="inclusionText">
                        Transfers and local transport
                      </span>
                    </div>

                    <div className="inclusionItem">
                      <span className="bulletDot" />
                      <span className="inclusionText">
                        Ganga Aarti experience
                      </span>
                    </div>

                    <div className="inclusionItem">
                      <span className="bulletDot" />
                      <span className="inclusionText">
                        Sarnath sightseeing as per <br />
                        itinerary
                      </span>
                    </div>

                    <div className="inclusionItem">
                      <span className="bulletDot" />
                      <span className="inclusionText">
                        All applicable taxes
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </Grid>

            {/* Right Side: Send an enquiry card */}
            <Grid size={{ xs: 12, md: 5, lg: 4.8 }}>
              <div className="enquiryCardBox">
                <h3 className="enquiryTitle">Send an enquiry</h3>
                <p className="enquirySubtitle">
                  Share your details and our yatra desk will call you back.
                </p>

                {!enquirySubmitted ? (
                  <form onSubmit={handleEnquirySubmit} className="enquiryForm">
                    {enquiryError && (
                      <div className="errorAlert">{enquiryError}</div>
                    )}

                    <div className="formGroup">
                      <input
                        type="text"
                        placeholder="Your name"
                        value={enquiryName}
                        onChange={(e) => setEnquiryName(e.target.value)}
                      />
                    </div>

                    <div className="formGroup">
                      <input
                        type="tel"
                        placeholder="Mobile number"
                        value={enquiryPhone}
                        onChange={(e) => setEnquiryPhone(e.target.value)}
                      />
                    </div>

                    <div className="formGroup">
                      <input
                        type="text"
                        placeholder="Package"
                        value={enquiryPackage}
                        onChange={(e) => setEnquiryPackage(e.target.value)}
                      />
                    </div>

                    <div className="formGroup">
                      <textarea
                        rows="4"
                        placeholder="Travel dates, number of yatris..."
                        value={enquiryNotes}
                        onChange={(e) => setEnquiryNotes(e.target.value)}
                      />
                    </div>

                    <button type="submit" className="submitBtn">
                      Submit enquiry
                    </button>
                  </form>
                ) : (
                  <div className="submittedState">
                    <CheckCircle className="checkIcon" />
                    <h4>Thank You, {enquiryName}!</h4>
                    <p>
                      Your enquiry for <strong>{enquiryPackage}</strong> has been received.
                      Our yatra desk will call you shortly on <strong>{enquiryPhone}</strong>.
                    </p>
                    <button
                      type="button"
                      className="resetBtn"
                      onClick={() => {
                        setEnquirySubmitted(false);
                        setEnquiryName('');
                        setEnquiryPhone('');
                        setEnquiryNotes('');
                      }}
                    >
                      Send Another Enquiry
                    </button>
                  </div>
                )}
              </div>
            </Grid>
          </Grid>
        </Container>
      </section>

      {/* ==========================================
          VARANASI TOUR PACKAGES INFO SECTION
      ========================================== */}
      <section className="vnpInfoSection pb-0">
        <Container maxWidth="lg">
          <div className="infoSectionHeader">
            <h2 className="infoSectionTitle">Kashi Vishwanath tour packages</h2>
            <p className="infoSectionLead">
              Some pilgrimages are planned around a single sacred moment, while others become a journey through the city itself. Kashi Vishwanath is both — a destination where ancient traditions, temple rituals and the timeless presence of the Ganga come together.
            </p>
          </div>

          <Grid container spacing={{ xs: 3, md: 5 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <div className="infoBlock">
                <h3 className="infoBlockTitle">Beginning the journey in Varanasi</h3>
                <p className="infoBlockText">
                  For most pilgrims, Varanasi is the gateway to Kashi Vishwanath. The city's narrow lanes, ancient ghats and vibrant markets create a unique atmosphere around the pilgrimage. A well-planned Kashi package makes it easier to explore the temple and experience the spiritual side of the city.
                </p>
              </div>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <div className="infoBlock">
                <h3 className="infoBlockTitle">Darshan: the heart of the pilgrimage</h3>
                <p className="infoBlockText">
                  For devotees, visiting Kashi Vishwanath is more than simply entering a temple. The darshan of Lord Shiva, the temple rituals and prayers create a deeply personal experience. Many pilgrims also visit the nearby ghats and take part in the sacred traditions along the Ganga.
                </p>
              </div>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <div className="infoBlock">
                <h3 className="infoBlockTitle">When convenience matters</h3>
                <p className="infoBlockText">
                  For senior citizens, families and travellers with limited time, guided packages can make the journey easier. Temple visits, accommodation, local transfers and sightseeing can be arranged together, allowing devotees to focus more on their spiritual experience.
                </p>
              </div>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <div className="infoBlock">
                <h3 className="infoBlockTitle">A pilgrimage beyond generations</h3>
                <p className="infoBlockText">
                  Kashi has welcomed pilgrims for generations. Grandparents, parents and children often travel together to experience its temples, ghats and traditions. For many families, returning to Kashi becomes a meaningful tradition passed from one generation to the next.
                </p>
              </div>
            </Grid>
          </Grid>
        </Container>
      </section>

      {/* ==========================================
          FAQS ON KASHI VISHWANATH TOUR PACKAGES SECTION
      ========================================== */}
      <section className="vnpFaqSection">
        <Container maxWidth="md">
          <h2 className="faqSectionTitle">FAQs on Kashi Vishwanath tour packages</h2>

          <div className="faqCard">
            {faqs.map((faq) => {
              const isOpen = openFaq === faq.id;
              return (
                <div
                  key={faq.id}
                  className={`faqItem ${isOpen ? 'open' : ''}`}
                >
                  <button
                    type="button"
                    className="faqQuestionBtn"
                    onClick={() => toggleFaq(faq.id)}
                  >
                    <span className="faqQuestionText">{faq.question}</span>
                    <KeyboardArrowDown
                      className={`faqArrowIcon ${isOpen ? 'rotated' : ''}`}
                    />
                  </button>

                  {isOpen && (
                    <div className="faqAnswerBox">
                      <p className="faqAnswerText">{faq.answer}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Container>
      </section>
    </div>
  );
}

export default VaranasiPackage;
